import React from 'react';
import { X, Heart, Share2, MapPin, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { CommunityBadge } from '@/components/camps/CommunityBadge';
import { getCategoryEmoji } from '@/utils/emojiHelpers';

/**
 * Program Header Component with Hero Image
 * 
 * Displays a stunning hero image section with program information overlaid
 * 
 * @param {Object} props
 * @param {Object} props.camp - Camp data
 * @param {string} props.mode - 'discovery' | 'user_program'
 * @param {Object} [props.userProgram] - User program data (for user_program mode)
 * @param {Function} [props.onSaveForLater] - Save handler (for discovery mode)
 * @param {Function} [props.onShare] - Share handler
 * @param {Function} [props.onGetDirections] - Get directions handler
 * @param {Function} [props.onClose] - Close modal handler
 */
const ProgramHeader = ({
  camp,
  mode,
  userProgram = null,
  onSaveForLater = null,
  onShare = null,
  onGetDirections = null,
  onClose = null,
}) => {
  // Get hero image - try multiple sources
  const getHeroImage = () => {
    // Try photos array first
    if (camp.photos && Array.isArray(camp.photos) && camp.photos.length > 0) {
      const firstPhoto = camp.photos[0];
      // Handle different photo object structures
      if (typeof firstPhoto === 'string') {
        return firstPhoto;
      }
      if (firstPhoto.url) {
        return firstPhoto.url;
      }
      if (firstPhoto.photoUrl) {
        return firstPhoto.photoUrl;
      }
    }
    
    // Try image_url
    if (camp.image_url) {
      return camp.image_url;
    }
    
    // Default fallback - use gradient instead of broken image
    return null;
  };

  const heroImage = getHeroImage();
  const hasImage = heroImage !== null;

  // Icon Button Component
  const IconButton = ({ icon: Icon, label, onClick, className = '' }) => (
    <button
      onClick={onClick}
      className={`
        w-10 h-10 rounded-full
        bg-white/20 backdrop-blur-sm
        border border-white/30
        flex items-center justify-center
        text-white
        hover:bg-white/30 hover:border-white/40
        transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent
        ${className}
      `}
      aria-label={label}
      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
    >
      <Icon className="w-5 h-5" />
    </button>
  );

  // Status Pill Component (for user_program mode)
  const StatusPill = ({ status }) => {
    if (!status) return null;
    
    const statusColors = {
      'booked': 'bg-green-500/80',
      'considering': 'bg-yellow-500/80',
      'saved': 'bg-blue-500/80',
      'favorite': 'bg-pink-500/80',
    };

    return (
      <Badge
        className={`
          ${statusColors[status] || 'bg-white/20'}
          backdrop-blur-sm text-white border-white/30 rounded-full px-3 py-1 text-xs font-medium capitalize
        `}
        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
      >
        {status}
      </Badge>
    );
  };

  const textShadowStyle = { textShadow: '0 2px 4px rgba(0,0,0,0.3)' };

  return (
    <div
      className="relative h-48 md:h-64 w-full overflow-hidden"
      role="banner"
      aria-label="Program header"
    >
      {/* Hero Image */}
      {hasImage ? (
        <img
          src={heroImage}
          alt={camp.name || 'Program image'}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          onError={(e) => {
            // Fallback to gradient with emoji if image fails to load
            e.target.style.display = 'none';
            const parent = e.target.parentElement;
            if (parent && !parent.querySelector('.gradient-fallback')) {
              const fallback = document.createElement('div');
              fallback.className = 'gradient-fallback absolute inset-0 bg-gradient-to-br from-brand-600 via-brand-500 to-blue-500 flex items-center justify-center';
              const emojiSpan = document.createElement('span');
              emojiSpan.className = 'text-6xl md:text-8xl';
              emojiSpan.setAttribute('role', 'img');
              emojiSpan.setAttribute('aria-label', `${camp.name || 'Program'} type`);
              emojiSpan.textContent = getCategoryEmoji(camp);
              fallback.appendChild(emojiSpan);
              parent.appendChild(fallback);
            }
          }}
        />
      ) : (
        /* Gradient Fallback with Emoji */
        <div className="absolute inset-0 bg-gradient-to-br from-brand-600 via-brand-500 to-blue-500 flex items-center justify-center">
          <span className="text-6xl md:text-8xl" role="img" aria-label={`${camp.name || 'Program'} type`}>
            {getCategoryEmoji(camp)}
          </span>
        </div>
      )}

      {/* Gradient Overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

      {/* Close Button (top right) */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/30 hover:border-white/40 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent"
          aria-label="Close modal"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {/* Content on top of image */}
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-white">
        {/* Camp Name and Action Buttons */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <h1
              className="text-xl md:text-2xl lg:text-3xl font-bold leading-tight"
              style={textShadowStyle}
            >
              {camp.name || camp.title || 'Untitled Program'}
            </h1>
            {/* Organization */}
            {(camp.organization || (userProgram?.program_data?.organization)) && (
              <div className="text-sm md:text-base mt-1 opacity-90" style={textShadowStyle}>
                {camp.organization || userProgram?.program_data?.organization}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {mode === 'discovery' ? (
              <IconButton
                icon={Heart}
                label="Save for Later"
                onClick={onSaveForLater}
              />
            ) : (
              <StatusPill status={userProgram?.status} />
            )}
            <IconButton
              icon={Share2}
              label="Share"
              onClick={onShare}
            />
          </div>
        </div>

        {/* Badges Row */}
        <div className="flex items-center gap-2 md:gap-3 flex-wrap mb-3">
          {/* Rating Badge */}
          {camp.rating !== undefined && camp.rating !== null && (
            <Badge
              className="bg-white/20 backdrop-blur-sm text-white border-white/30 rounded-full px-3 py-1 text-xs font-medium"
              style={textShadowStyle}
            >
              <Star className="w-3 h-3 inline mr-1 fill-yellow-400 text-yellow-400" />
              {camp.rating.toFixed(1)}
              {camp.user_ratings_total && (
                <span className="ml-1 opacity-90">
                  ({camp.user_ratings_total} {camp.user_ratings_total === 1 ? 'review' : 'reviews'})
                </span>
              )}
            </Badge>
          )}

          {/* Program Type Badge */}
          {camp.program_type && (
            <Badge
              className="bg-white/20 backdrop-blur-sm text-white border-white/30 rounded-full px-3 py-1 text-xs font-medium"
              style={textShadowStyle}
            >
              {camp.program_type}
            </Badge>
          )}

          {/* Community Badge */}
          <CommunityBadge camp={camp} variant="frosted" size="md" />
        </div>

        {/* Location Row */}
        {(() => {
          // Get location string - handle different formats
          let locationString = null;
          
          if (camp.location) {
            if (typeof camp.location === 'string') {
              locationString = camp.location;
            } else if (typeof camp.location === 'object') {
              // If location is an object, try to get a string representation
              locationString = camp.location.formatted_address || 
                              camp.location.address || 
                              camp.location.name ||
                              camp.formatted_address ||
                              camp.general_location ||
                              null;
            }
          } else {
            // Fallback to other location fields
            locationString = camp.formatted_address || 
                            camp.general_location || 
                            camp.address ||
                            null;
          }
          
          return locationString ? (
            <div className="flex items-center gap-2 text-sm md:text-base mt-3">
              <MapPin className="w-4 h-4 flex-shrink-0" style={textShadowStyle} />
              <span style={textShadowStyle} className="flex-1 min-w-0 truncate">
                {locationString}
              </span>
            {camp.distance !== undefined && camp.distance !== null && (
              <span className="opacity-80 whitespace-nowrap" style={textShadowStyle}>
                • {camp.distance} {camp.distance === 1 ? 'mile' : 'miles'}
              </span>
            )}
              {onGetDirections && (
                <button
                  onClick={onGetDirections}
                  className="underline ml-auto hover:no-underline transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent rounded px-1"
                  style={textShadowStyle}
                >
                  Get Directions →
                </button>
              )}
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
};

export default ProgramHeader;
